import { useState, ButtonHTMLAttributes, useCallback, useEffect, lazy, Suspense } from 'react';
import NavButton from './Navigation/NavButton.tsx';
import { Calc } from './Icons.tsx';
import Popover from './Popover.tsx';
import { useDesmos } from '../hooks/useDesmos.ts';

// desmos-react references the global `Desmos` at module-evaluation time, so it
// can only be imported after the Desmos script has loaded. Hence the dynamic
// import, rendered only once useDesmos reports ready (see below).
const GraphingCalculator = lazy(() =>
  import('desmos-react').then(m => ({ default: m.GraphingCalculator })),
);

const CalculatorButton = (props: ButtonHTMLAttributes<HTMLButtonElement>) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>();
  const click = useCallback(() => setOpen(x => !x), []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <NavButton {...props} ref={setAnchor} Icon={Calc} onClick={click}>calc</NavButton>
      <CalculatorDialog
        open={open}
        anchor={anchor}
        onClose={close}
      />
    </>
  );
};

interface DialogProps {
  open: boolean;
  onClose: () => void;
  anchor?: Element | null;
}

const CalculatorDialog = ({ open, anchor, onClose }: DialogProps) => {
  // Start loading the Desmos bundle once the dialog is first opened, and keep
  // it mounted afterwards (unmount={false}) so the calculator state persists.
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  const ready = useDesmos(hasOpened);

  return (
    <Popover
      draggable
      open={open}
      title="Calculator"
      anchor={anchor}
      placement="bottom-end"
      onClose={onClose}
      width="50rem"
      unmount={false}
      disableAwayClick
      full
    >
      {ready ? (
        <Suspense fallback={null}>
          <GraphingCalculator
            keypad={true}
            autosize
            folders={false}
            images={false}
            notes={false}
            links={false}
            attributes={{ className: 'calculator', style: { height: '70vh', maxHeight: '40rem' } }}
          />
        </Suspense>
      ) : null}
    </Popover>
  );
};

export default CalculatorButton;
